/*
  Bark & Beyond: the two things a customer needs while scrolling, plus instant cart feedback.

  The header deliberately does not stick, so once you scroll into a collection both the
  brand and the cart count are gone. This puts back only those two, as small pills that
  appear after the header scrolls off: the wordmark top left, the cart count top right.

  It also removes the two waits around adding to cart. Shopify's /cart/add round trip is
  roughly 700ms, so without help the count sits unchanged and the drawer stays shut for
  most of a second after the click. Here the count moves on submit and the drawer opens
  on submit, showing a skeleton until the real contents arrive.

  Truth always comes from /cart.js, never from re-reading our own optimistic markup. An
  earlier version parsed the bubble it had just written and read "1" next to "1 item" as
  eleven, which is why the count flashed 11 before settling to 1.

  Uses scroll events and MutationObserver, never IntersectionObserver: none of this may
  depend on an element being observed into view.
*/
(function () {
  'use strict';
  if (window.__bnbFloat) return;
  window.__bnbFloat = true;
  if (/^\/(cart|checkout)/.test(window.location.pathname)) return;

  /* ---------- styles ---------- */
  var style = document.createElement('style');
  style.textContent = [
    '.bnb-cartfloat{position:fixed;top:14px;right:14px;z-index:900;display:flex;',
    'align-items:center;gap:7px;padding:9px 14px 9px 11px;border-radius:999px;',
    'background:#24354F;color:#EEF3F9;text-decoration:none;font:inherit;font-size:13px;',
    'font-weight:700;line-height:1;box-shadow:0 4px 14px rgba(28,43,69,.22);',
    'opacity:0;visibility:hidden;transform:translateY(-8px);',
    'transition:opacity .18s ease,transform .18s ease,visibility .18s;}',
    '.bnb-cartfloat.is-on{opacity:1;visibility:visible;transform:none;}',
    '.bnb-cartfloat:hover{background:#1C2B45;color:#fff;}',
    '.bnb-cartfloat:focus-visible,.bnb-logofloat:focus-visible{outline:2px solid #9FB4CC;outline-offset:2px;}',
    '.bnb-cartfloat svg{width:17px;height:17px;flex:0 0 auto;}',
    '.bnb-cartfloat-n{min-width:17px;text-align:center;}',
    '.bnb-logofloat{position:fixed;top:14px;left:14px;z-index:900;display:flex;',
    'align-items:center;padding:7px 13px;border-radius:999px;background:#FFFFFF;',
    'box-shadow:0 4px 14px rgba(28,43,69,.18);line-height:0;',
    'opacity:0;visibility:hidden;transform:translateY(-8px);',
    'transition:opacity .18s ease,transform .18s ease,visibility .18s;}',
    '.bnb-logofloat.is-on{opacity:1;visibility:visible;transform:none;}',
    '.bnb-logofloat img{height:20px;width:auto;display:block;}',
    /* the drawer opens straight away; this stands in until the real contents land */
    /* While the drawer is waiting it must not look like an empty cart. The theme hides
       its own "Your cart" heading whenever the cart is empty, so with the empty message
       suppressed the panel was simply blank white, which reads as "there is nothing in
       here" just as strongly as the words did. The pending state carries its own
       heading and a skeleton solid enough to see. */
    'cart-drawer.bnb-pending .drawer__inner-empty{display:none!important;}',
    '.bnb-skel{padding:20px 18px;}',
    '.bnb-skel-head{font-weight:700;font-size:15px;color:#24354F;margin:0 0 4px;}',
    '.bnb-skel-sub{font-size:13px;color:#24354F;opacity:.62;margin:0 0 18px;}',
    '.bnb-skel-row{display:flex;gap:13px;align-items:center;margin-bottom:16px;}',
    /* Every block also carries the theme's own .block-when-empty class. base.css has a
       blanket `div:empty{display:none}` and these placeholders are empty divs by
       definition, so they were being removed outright: that is why the loading drawer
       looked blank white rather than merely faint.
       A solid colour under the sheen, not the sheen alone. The gradient starts at
       background-position 200%, i.e. off the block, so wherever the animation does not
       run (reduced motion, a backgrounded tab, a browser that throttles it) a
       gradient-only block paints as nothing and the drawer looks blank again. */
    '.bnb-skel-b{background-color:rgba(36,53,79,.13);',
    'background-image:linear-gradient(90deg,rgba(36,53,79,0),rgba(36,53,79,.13),rgba(36,53,79,0));',
    'background-repeat:no-repeat;background-size:200% 100%;border-radius:8px;',
    'animation:bnb-sheen 1.1s ease-in-out infinite;}',
    '.bnb-skel-img{width:66px;height:66px;flex:0 0 auto;border-radius:10px;}',
    '.bnb-skel-l1{height:13px;width:74%;margin-bottom:9px;}',
    '.bnb-skel-l2{height:13px;width:44%;}',
    '@keyframes bnb-sheen{0%{background-position:200% 0}100%{background-position:-200% 0}}',
    '@media (prefers-reduced-motion:reduce){.bnb-cartfloat,.bnb-logofloat{transition:none;}',
    '.bnb-skel-b{animation:none;}}',
    '@media screen and (max-width:749px){.bnb-cartfloat{top:10px;right:10px;padding:8px 12px 8px 10px;font-size:12.5px;}',
    '.bnb-logofloat{top:10px;left:10px;padding:6px 11px;}.bnb-logofloat img{height:17px;}}'
  ].join('');
  document.head.appendChild(style);

  /* ---------- the two pills ---------- */
  var pill = document.createElement('a');
  pill.className = 'bnb-cartfloat';
  pill.href = '/cart';
  pill.setAttribute('data-no-instant', '');
  pill.innerHTML =
    '<svg viewBox="0 0 512 512" aria-hidden="true" fill="none" stroke="currentColor" ' +
    'stroke-width="34" stroke-linecap="round" stroke-linejoin="round">' +
    '<circle cx="176" cy="416" r="16"/><circle cx="400" cy="416" r="16"/>' +
    '<polyline points="48,80 112,80 160,336 416,336"/>' +
    '<path d="M160 288h249.44a8 8 0 0 0 7.85-6.43l28.8-144a8 8 0 0 0-7.85-9.57H128"/></svg>' +
    '<span class="bnb-cartfloat-n" aria-hidden="true">0</span>' +
    '<span class="visually-hidden"></span>';
  var numEl = pill.querySelector('.bnb-cartfloat-n');
  var srEl = pill.querySelector('.visually-hidden');

  var logo = document.createElement('a');
  logo.className = 'bnb-logofloat';
  logo.href = '/';
  logo.setAttribute('aria-label', 'Home');
  logo.setAttribute('data-no-instant', '');
  var logoImg = document.createElement('img');
  logoImg.alt = '';
  logoImg.decoding = 'async';
  logo.appendChild(logoImg);

  function logoSource() {
    var h = document.querySelector('.header__heading-logo');
    if (h && (h.currentSrc || h.src)) return h.currentSrc || h.src;
    var tag = document.querySelector('script[src*="bnb-cart-float"]');
    return tag ? tag.getAttribute('data-logo') : null;
  }

  /* ---------- count ---------- */
  var shown = 0;

  // Read a bubble the theme rendered. Never one of ours: an optimistic node we wrote is
  // marked, and its text is not a reliable source for a number.
  function realBubble() { return document.querySelector('.cart-count-bubble:not([data-bnb-opt])'); }
  function readBubble(el) {
    if (!el) return null;
    var span = el.querySelector('span[aria-hidden="true"]');
    var text = span ? span.textContent : el.textContent;
    var m = (text || '').match(/\d+/);
    return m ? parseInt(m[0], 10) : null;
  }

  function render() {
    numEl.textContent = shown;
    srEl.textContent = shown === 1 ? '1 item in cart' : shown + ' items in cart';
    pill.setAttribute('aria-label', srEl.textContent);
    place();
  }

  // Optimistic display only. The number is never read back from here.
  function paintHeader(n) {
    var real = realBubble();
    if (real) {
      var s = real.querySelector('span[aria-hidden="true"]');
      if (s) s.textContent = n;
      return;
    }
    var mine = document.querySelector('.cart-count-bubble[data-bnb-opt]');
    if (mine) { mine.querySelector('span').textContent = n; return; }
    var link = document.querySelector('#cart-icon-bubble');
    if (!link) return;
    var made = document.createElement('div');
    made.className = 'cart-count-bubble font-bold';
    made.setAttribute('data-bnb-opt', '');
    // Note the space between the spans. Without it this element's text content reads
    // "11 item", which is what an earlier version parsed back as eleven. The visible
    // span is the first one; the second is clipped by the theme's .visually-hidden.
    made.innerHTML = '<span aria-hidden="true">' + n + '</span> ' +
                     '<span class="visually-hidden">' + n + (n === 1 ? ' item' : ' items') + '</span>';
    link.appendChild(made);
  }
  function dropOptimistic() {
    var mine = document.querySelector('.cart-count-bubble[data-bnb-opt]');
    if (mine && realBubble()) mine.remove();
  }

  // /cart.js is the only source of truth.
  var lastSync = 0, syncTimer = null;
  function sync(delay) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      lastSync = Date.now();
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          if (typeof c.item_count === 'number') { shown = c.item_count; render(); }
          dropOptimistic();
        })
        .catch(function () {});
    }, delay || 250);
  }

  /* ---------- placement ---------- */
  var headerEl = null;
  function header() {
    if (!headerEl || !headerEl.isConnected) {
      headerEl = document.querySelector('.header-wrapper') || document.querySelector('header');
    }
    return headerEl;
  }
  function place() {
    var past = true, h = header();
    if (h) past = h.getBoundingClientRect().bottom <= 0;
    pill.classList.toggle('is-on', shown > 0 && past);
    logo.classList.toggle('is-on', past);
  }

  /* ---------- the drawer opens on click, not on response ---------- */
  var skeleton = null, settleTimer = null;
  function openPending() {
    var d = document.querySelector('cart-drawer');
    if (!d) return;
    d.classList.add('bnb-pending');
    var host = d.querySelector('cart-drawer-items') || d.querySelector('.drawer__inner') || d;
    if (!d.querySelector('.cart-item') && !skeleton) {
      skeleton = document.createElement('div');
      skeleton.className = 'bnb-skel';
      skeleton.setAttribute('aria-hidden', 'true');
      skeleton.innerHTML =
        '<p class="bnb-skel-head">Your cart</p>' +
        '<p class="bnb-skel-sub">Adding your item\u2026</p>' +
        '<div class="bnb-skel-row"><div class="bnb-skel-b block-when-empty bnb-skel-img"></div>' +
        '<div style="flex:1"><div class="bnb-skel-b block-when-empty bnb-skel-l1"></div>' +
        '<div class="bnb-skel-b block-when-empty bnb-skel-l2"></div></div></div>' +
        '<div class="bnb-skel-row"><div class="bnb-skel-b block-when-empty bnb-skel-img" style="opacity:.55"></div>' +
        '<div style="flex:1"><div class="bnb-skel-b block-when-empty bnb-skel-l1" style="opacity:.55"></div>' +
        '<div class="bnb-skel-b block-when-empty bnb-skel-l2" style="opacity:.55"></div></div></div>';
      host.appendChild(skeleton);
    }
    if (typeof d.open === 'function') { try { d.open(); } catch (e) {} }
    clearTimeout(settleTimer);
    settleTimer = setTimeout(settle, 8000);      // never leave a spinner stuck
  }
  function settle() {
    clearTimeout(settleTimer);
    var d = document.querySelector('cart-drawer');
    if (d) d.classList.remove('bnb-pending');
    if (skeleton) { skeleton.remove(); skeleton = null; }
  }

  /* ---------- wiring ---------- */
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || !f.getAttribute || !/\/cart\/add/.test(f.getAttribute('action') || '')) return;
    var q = parseInt((f.querySelector('[name="quantity"]') || {}).value, 10);
    shown += (isNaN(q) || q < 1) ? 1 : q;
    render();
    paintHeader(shown);
    openPending();
    sync(600);
  }, true);

  pill.addEventListener('click', function (e) {
    var d = document.querySelector('cart-drawer');
    if (d && typeof d.open === 'function') { e.preventDefault(); d.open(); }
  });

  function start() {
    document.body.appendChild(pill);
    var src = logoSource();
    if (src) { logoImg.src = src; document.body.appendChild(logo); }

    var b = readBubble(realBubble());
    shown = b === null ? 0 : b;
    render();

    var host = document.querySelector('#cart-icon-bubble');
    host = host ? (host.closest('.shopify-section') || host.parentNode) : document.body;
    new MutationObserver(function () {
      if (Date.now() - lastSync > 200) sync(120);   // the theme re-rendered; confirm with the server
    }).observe(host, { childList: true, subtree: true, characterData: true });

    // The skeleton clears only when the drawer actually has contents, never merely
    // because something mutated. An earlier version settled on any mutation, and since
    // painting the optimistic count mutates the header immediately, the skeleton was
    // torn down at once and the customer watched an empty drawer for a full second.
    var drawerEl = document.querySelector('cart-drawer');
    if (drawerEl) {
      new MutationObserver(function () {
        if (drawerEl.querySelector('.cart-item')) settle();
      }).observe(drawerEl, { childList: true, subtree: true });
    }

    addEventListener('scroll', place, { passive: true });
    addEventListener('resize', place, { passive: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else { start(); }
})();
